import { BaseService } from './base.service';
import { db } from '../data_oscbase';

export class CustomerService extends BaseService {
  constructor() {
    super('customers');
  }

  /**
   * Vérifie si un client peut être modifié ou supprimé
   * @param {Object} customer - Le client à vérifier
   * @returns {boolean} - true si le client peut être modifié/supprimé
   */
  isCustomerEditable(customer) {
    // Le client "Comptant" (ID=1) ne peut pas être modifié ni supprimé
    return !(customer?.id === 1 || customer?.name?.toLowerCase() === 'client comptant');
  }

  /**
   * Crée un nouveau client
   * @param {Object} customerData - Données du client
   * @returns {Promise<Object>} - Client créé
   */
  async createCustomer(customerData) {
    try {
      const newCustomer = await this.create(customerData);
      return newCustomer;
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
      throw error;
    }
  }

  /**
   * Met à jour les informations d'un client
   * @param {number} customerId - ID du client
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Client mis à jour
   */
  async updateCustomer(customerId, updates) {
    try {
      const customer = await this.getById(customerId);
      if (!this.isCustomerEditable(customer)) {
        throw new Error('Ce client ne peut pas être modifié');
      }
      const updatedCustomer = await this.update(customerId, updates);
      return updatedCustomer;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du client:', error);
      throw error;
    }
  }

  /**
   * Supprime un client s'il n'a pas de dépendances
   * @param {number} customerId - ID du client à supprimer
   * @returns {Promise<boolean>} - true si supprimé, false si impossible
   */
  async deleteCustomer(customerId) {
    try {
      const customer = await this.getById(customerId);
      if (!this.isCustomerEditable(customer)) {
        throw new Error('Ce client ne peut pas être supprimé');
      }

      // Vérifier les dépendances dans les autres tables
      
      // Vérifier les ventes liées
      const sales = await db.sales.where('customerId').equals(customerId).toArray();
      if (sales.length > 0) {
        throw new Error('Impossible de supprimer le client car il a des ventes associées');
      }

      // Vérifier les rendez-vous liés
      const appointments = await db.appointments.where('customerId').equals(customerId).toArray();
      if (appointments.length > 0) {
        throw new Error('Impossible de supprimer le client car il a des rendez-vous associés');
      }

      // Vérifier les commandes liées
      const commandes = await db.commandes.where('customerId').equals(customerId).toArray();
      if (commandes.length > 0) {
        throw new Error('Impossible de supprimer le client car il a des commandes associées');
      }

      // Si aucune dépendance n'est trouvée, supprimer le client
      await this.delete(customerId);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du client:', error);
      throw error;
    }
  }

  /**
   * Récupère les clients actifs
   * @returns {Promise<Array>} - Liste des clients actifs
   */
  async getActiveCustomers() {
    try {
      return await this.getByField('isActive', true);
    } catch (error) {
      console.error('Erreur lors de la récupération des clients actifs:', error);
      throw error;
    }
  }

  /**
   * Recherche des clients selon un critère
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des clients correspondants
   */
  async searchCustomers(query) {
    try {
      return await this.search(query, ['name', 'email', 'phone']);
    } catch (error) {
      console.error('Erreur lors de la recherche des clients:', error);
      throw error;
    }
  }

  /**
   * Récupère tous les clients
   * @returns {Promise<Array>} - Liste de tous les clients
   */
  async getAllCustomers() {
    try {
      return await this.getAll();
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error);
      throw error;
    }
  }
} 